import { observer } from 'mobx-react-lite';

import { Icons } from '@/assets/icons/icons';
import IMessageLogo from '@/assets/images/channels/imessage.png';
import WhatsAppLogo from '@/assets/images/channels/whatsapp.png';
import CopyButton from '@/components/blocks/CopyButton';
import { ModalsManager } from '@/components/modals/modalsManager';
import { BetaTag } from '@/components/ui/beta-tag';
import { Button } from '@/components/ui/Button';
import { Box, XStack, YStack } from '@/components/ui/layout/layout';
import { AgentInboxModal } from '@/features/agent-inbox/agent-inbox-modal';
import { useAgentInboxAddressQuery } from '@/features/agent-inbox/agent-inbox.query';
import { FEATURES, useFeature } from '@/features/feature-flags';
import { ChannelPersonaEditor } from '@/features/msg-channels/common/channel-persona-editor';
import { openChannelsBetaFeedback } from '@/features/msg-channels/common/msg-channels.feedback';
import {
  MESSAGING_CHANNEL_DESCRIPTIONS,
  MessagingChannelTypes,
  WHATSAPP_REPLY_WINDOW_NOTE,
  type MessagingChannelType,
} from '@/features/msg-channels/common/msg-channels.types';
import { useIMessageChannelQuery } from '@/features/msg-channels/imessage/imessage.query';
import { IMessageChannelConnectModal } from '@/features/msg-channels/imessage/IMessageChannelConnectModal';
import { useWhatsAppChannelQuery } from '@/features/msg-channels/whatsapp/whatsapp.query';
import { WhatsAppChannelConnectModal } from '@/features/msg-channels/whatsapp/WhatsAppChannelConnectModal';
import { userStore } from '@/stores/user.store';

import { SettingsSection } from '../settings-section';

import { ChannelChatVisibilitySetting } from './channel-chat-visibility-setting';

type ChannelRowProps = {
  channelType: MessagingChannelType;
  name: string;
  description: string;
  identifier?: string;
  iconSrc: string;
  isChecking: boolean;
  isLoadingError: boolean;
  isRefetchError: boolean;
  onClick: () => void;
  onRetry: () => void;
};

const ChannelRow = ({
  channelType,
  name,
  description,
  identifier,
  iconSrc,
  isChecking,
  isLoadingError,
  isRefetchError,
  onClick,
  onRetry,
}: ChannelRowProps) => {
  const isConnected = Boolean(identifier);
  const buttonLabel = isChecking
    ? 'Checking...'
    : isLoadingError
      ? 'Retry'
      : isConnected
        ? 'Manage'
        : 'Connect';

  return (
    <XStack
      data-testid={`channel-row-${channelType}`}
      className="items-center justify-between gap-6 rounded-lg border border-border bg-surface px-4 py-3.5"
    >
      <XStack className="min-w-0 items-center gap-3.5">
        <Box className="grid size-10 shrink-0 place-items-center">
          <img
            src={iconSrc}
            alt=""
            className="block size-8 rounded-[0.45rem] object-cover"
          />
        </Box>
        <YStack className="min-w-0 gap-1">
          <p className="text-sm font-medium text-foreground">{name}</p>
          <p
            data-testid={`channel-status-${channelType}`}
            className="truncate text-xs text-foreground-muted"
          >
            {identifier ??
              (isLoadingError
                ? "Couldn't check connection status"
                : description)}
          </p>
          {isRefetchError && (
            <p className="truncate text-xs text-danger-11">
              Couldn't refresh connection status
            </p>
          )}
        </YStack>
      </XStack>
      <Button
        data-testid={`channel-action-button-${channelType}`}
        variant={isConnected ? 'outline' : 'default'}
        size="sm"
        onClick={isLoadingError ? onRetry : onClick}
        disabled={isChecking}
      >
        {buttonLabel}
      </Button>
    </XStack>
  );
};

const IMessageChannelRow = () => {
  const {
    data: connectionData,
    isLoading,
    isLoadingError,
    isRefetchError,
    isFetching,
    refetch,
  } = useIMessageChannelQuery();
  return (
    <ChannelRow
      channelType={MessagingChannelTypes.IMESSAGE}
      name="iMessage"
      description={
        MESSAGING_CHANNEL_DESCRIPTIONS[MessagingChannelTypes.IMESSAGE]
      }
      identifier={connectionData?.identifier}
      iconSrc={IMessageLogo}
      isChecking={isLoading || (isLoadingError && isFetching)}
      isLoadingError={isLoadingError}
      isRefetchError={isRefetchError}
      onRetry={() => void refetch()}
      onClick={() =>
        ModalsManager.show(IMessageChannelConnectModal, {
          currentIdentifier: connectionData?.identifier ?? null,
        })
      }
    />
  );
};

const WhatsAppChannelRow = () => {
  const {
    data: connectionData,
    isLoading,
    isLoadingError,
    isRefetchError,
    isFetching,
    refetch,
  } = useWhatsAppChannelQuery();
  return (
    <ChannelRow
      channelType={MessagingChannelTypes.WHATSAPP_BUSINESS}
      name="WhatsApp"
      description={
        MESSAGING_CHANNEL_DESCRIPTIONS[MessagingChannelTypes.WHATSAPP_BUSINESS]
      }
      identifier={connectionData?.identifier}
      iconSrc={WhatsAppLogo}
      isChecking={isLoading || (isLoadingError && isFetching)}
      isLoadingError={isLoadingError}
      isRefetchError={isRefetchError}
      onRetry={() => void refetch()}
      onClick={() =>
        ModalsManager.show(WhatsAppChannelConnectModal, {
          currentIdentifier: connectionData?.identifier ?? null,
        })
      }
    />
  );
};

const AgentInboxRow = () => {
  const { data: address, isError } = useAgentInboxAddressQuery();
  return (
    <XStack
      data-testid="agent-inbox-row"
      className="items-center justify-between gap-6 rounded-lg border border-border bg-surface px-4 py-3.5"
    >
      <XStack className="min-w-0 items-center gap-3.5">
        <Box className="grid size-10 shrink-0 place-items-center">
          <Box className="grid size-8 place-items-center rounded-[0.45rem] bg-brand-jordy-blue text-brand-midnight-blue">
            <Icons.mail className="size-[1.125rem]" />
          </Box>
        </Box>
        <YStack className="min-w-0 gap-1">
          <p className="text-sm font-medium text-foreground">Email</p>
          <XStack className="h-4 min-w-0 items-center gap-1">
            <p className="truncate text-xs text-foreground-muted">
              {address ??
                (isError
                  ? "Couldn't load your assistant's address"
                  : 'Your assistant gets its own email address')}
            </p>
            {address && (
              <CopyButton
                text={address}
                copyText="Copy address"
                aria-label="Copy address"
                size="xs"
                className="-my-1"
              />
            )}
          </XStack>
        </YStack>
      </XStack>
      <Button
        data-testid="agent-inbox-manage-button"
        variant="outline"
        size="sm"
        onClick={() => ModalsManager.show(AgentInboxModal, {})}
      >
        Manage
      </Button>
    </XStack>
  );
};

const ChannelsBetaCallout = () => (
  <p className="mt-auto pt-6 text-sm text-foreground-muted">
    Channels is in beta and we&apos;re continuing to improve it.{' '}
    <button
      className="rounded-sm text-foreground focus-ring"
      onClick={() => openChannelsBetaFeedback('settings')}
    >
      <Icons.mail className="mr-1 inline size-3.5 align-[-0.15em]" />
      <span className="underline underline-offset-2">Email us</span>
    </button>{' '}
    with any issues or suggestions.
  </p>
);

export const ChannelsSettings = observer(() => {
  const hasFeature = useFeature();
  return (
    <SettingsSection.Root className="min-h-full">
      <SettingsSection.Header>
        <SettingsSection.Title trailing={<BetaTag />}>
          Channels
        </SettingsSection.Title>
        <SettingsSection.Description>
          {hasFeature(FEATURES.AGENT_INBOX)
            ? 'Chat with Littlebird from iMessage, WhatsApp or email.'
            : 'Chat with Littlebird from iMessage or WhatsApp.'}
        </SettingsSection.Description>
      </SettingsSection.Header>

      <YStack className="gap-3">
        <IMessageChannelRow />
        <WhatsAppChannelRow />
        {hasFeature(FEATURES.AGENT_INBOX) && <AgentInboxRow />}
        <p className="text-xs text-foreground-muted">
          <strong>Note:</strong> {WHATSAPP_REPLY_WINDOW_NOTE}
        </p>
      </YStack>

      {userStore.isInternalUser && (
        <>
          <ChannelChatVisibilitySetting />
          <ChannelPersonaEditor description="Persona applies to every connected messaging channel." />
        </>
      )}

      <ChannelsBetaCallout />
    </SettingsSection.Root>
  );
});
